/**
 * Phase 4 — connectivity state.
 *
 * navigator.onLine is a coarse signal (it just reflects whether the OS
 * thinks an interface is up), so we layer a real probe on top: when the
 * 'online' event fires we ping a known endpoint and only flip the React
 * state to 'online' if it actually responds. Avoids the dashboard
 * showing "back online" while the user is still on a captive portal.
 *
 * Two surfaces:
 *   useConnectivity()      — React hook returning { online, lastChecked }.
 *   onConnectivityChange() — vanilla subscribe API for non-React code
 *                            (e.g. the queue drainer in offline-axios).
 */
import { useEffect, useState } from 'react';

// Light-weight reachability check. Use a real endpoint that returns
// quickly regardless of auth state (401 / 403 are fine — they prove
// the network round-trip worked). /api/users/me/kyc-tier is on the SW's
// cacheable list AND exists on the backend (Phase 1).
const PROBE_URL    = '/api/users/me/kyc-tier';
const PROBE_TIMEOUT = 4000;

let _state = {
  online:       typeof navigator === 'undefined' ? true : !!navigator.onLine,
  lastChecked:  Date.now(),
};
const _listeners = new Set();

function _setState(next) {
  if (next.online === _state.online) {
    _state = { ..._state, lastChecked: Date.now() };
    return;
  }
  _state = { ...next, lastChecked: Date.now() };
  for (const fn of _listeners) {
    try { fn(_state); } catch (_) { /* listener errors don't matter */ }
  }
}

/**
 * Verify the network really works. navigator.onLine is famously
 * unreliable (captive portals, VPN handshakes). A short HEAD-ish GET
 * against an endpoint we own is the cheapest reliable proof.
 */
async function _probe() {
  try {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), PROBE_TIMEOUT);
    const resp = await fetch(PROBE_URL, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      signal: ctrl.signal,
    });
    clearTimeout(t);
    // 401 is fine — we still have a working network; it just means
    // we're not logged in. The connectivity probe is about the
    // physical layer, not the auth state.
    return resp.status < 500;
  } catch (e) {
    return false;
  }
}

function _attachListeners() {
  if (typeof window === 'undefined') return;
  window.addEventListener('online', async () => {
    const ok = await _probe();
    _setState({ online: ok });
  });
  window.addEventListener('offline', () => {
    _setState({ online: false });
  });
}
_attachListeners();

/** React hook — re-renders on connectivity transitions. */
export function useConnectivity() {
  const [state, setState] = useState(_state);
  useEffect(() => {
    const fn = (s) => setState(s);
    _listeners.add(fn);
    return () => _listeners.delete(fn);
  }, []);
  return state;
}

export function onConnectivityChange(fn) {
  _listeners.add(fn);
  return () => _listeners.delete(fn);
}

export function getConnectivity() {
  return { ..._state };
}

/** Force a probe (used after queue drain attempts to refresh state). */
export async function probeNow() {
  const ok = await _probe();
  _setState({ online: ok });
  return ok;
}
